import {
  Component,
  computed,
  effect,
  ElementRef,
  input,
  model,
  output,
  signal,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIconButton } from '@angular/material/button';
import { MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatTooltip } from '@angular/material/tooltip';
import { DateTimeHelper } from '@vectronic/util';
import { deepEquals } from 'nx/src/utils/json-diff';
import { FilterHeaderComponent } from '../filter-header/filter-header.component';

@Component({
  selector: 'vectronic-input-filter-with-header',
  templateUrl: './input-filter-with-header.component.html',
  styleUrls: ['./input-filter-with-header.component.scss'],
  imports: [
    FilterHeaderComponent,
    FormsModule,
    MatFormField,
    MatIcon,
    MatIconButton,
    MatInput,
    MatLabel,
    MatTooltip,
    MatSuffix,
  ],
})
export class InputFilterWithHeaderComponent<T extends string | string[] | number[] | Date> {
  title = input.required<string>();
  showTitle = input(true);
  placeholder = input('');
  titleTooltip = input('');
  stringToFilterConverter = input.required<(str: string) => T>();

  filterValue = model<T>(undefined);

  @ViewChild('filterInput') private filterInput: ElementRef<HTMLInputElement>;

  fieldValue = signal('');
  isFilterHidden = signal(true);
  filterEnabled = output<boolean>();
  confirmed = computed(() => {
    const filterValue = this.filterValue();
    if (filterValue instanceof Date) {
      const fieldValue = this.stringToFilterConverter()(this.fieldValue());
      if (fieldValue instanceof Date) {
        return filterValue.getTime() === fieldValue.getTime();
      }
      return false;
    }
    return deepEquals(filterValue, this.stringToFilterConverter()(this.fieldValue()));
  });

  // noinspection JSUnusedGlobalSymbols
  handleChangedFilterValue = effect(() => {
    const filterValue = this.filterValue();
    if ((Array.isArray(filterValue) && filterValue.length > 0) || filterValue) {
      this.isFilterHidden.set(false);
    }
    this.fieldValue.set(this.filterValueToField(this.filterValue()));
  });

  // noinspection JSUnusedGlobalSymbols
  handleHideTitle = effect(() => {
    if (!this.showTitle()) {
      this.isFilterHidden.set(false);
    }
  });

  // noinspection JSUnusedGlobalSymbols
  focusFilterInputOnShow = effect(() => {
    if (!this.isFilterHidden() && (this.fieldValue() === '' || this.fieldValue() === undefined)) {
      this.focus();
    }
  });

  toggleFilter() {
    const isHidden = this.isFilterHidden();
    if (isHidden) {
      this.isFilterHidden.set(false);
    } else {
      this.isFilterHidden.set(true);
      this.fieldValue.set('');
      this.confirm();
    }
    this.filterEnabled.emit(!this.isFilterHidden());
  }

  focus() {
    setTimeout(() => this.filterInput?.nativeElement.focus());
  }
  confirm() {
    if (!this.confirmed()) {
      this.filterValue.set(this.stringToFilterConverter()(this.fieldValue()));
    }
  }

  filterValueToField(filterValue: T): string {
    if (filterValue === undefined) {
      return '';
    }
    if (typeof filterValue === 'string') {
      return filterValue;
    }
    if (Array.isArray(filterValue)) {
      return filterValue.join(',');
    }
    if (filterValue instanceof Date) {
      return DateTimeHelper.formatDate(filterValue);
    }
  }

  static stringToStringFilterConverter(str: string): string {
    if (!str?.trim().length) {
      return undefined;
    }
    return str.trim();
  }

  private static readonly searchFieldSeparators: RegExp = /\s?und\s?|\s?and\s?|[,;]\s?|\s/i;
  static stringToNumberArrayFilterConverter(str: string): number[] {
    const split = str
      ?.split(InputFilterWithHeaderComponent.searchFieldSeparators)
      .filter((s) => s?.length > 0);
    if (!split?.length) {
      return undefined;
    }
    const numbers = split.map((s) => Number(s.trim())).filter((n) => !Number.isNaN(n));
    return numbers.length > 0 ? numbers : undefined;
  }

  static stringToStringArrayFilterConverter(str: string): string[] {
    const split = str
      ?.split(InputFilterWithHeaderComponent.searchFieldSeparators)
      .filter((s) => s?.length > 0);
    if (!split?.length) {
      return undefined;
    }
    return split.map((s) => s.trim());
  }

  static stringToDateConverter(str: string): Date {
    if (!str) {
      return undefined;
    }
    return DateTimeHelper.parseDate(str);
  }
}
